import { Button, Col, Input, Row, Image } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import React from 'react'
import { Field, Form } from 'react-final-form'
import {
  composeValidators,
  fileKbSizeValid,
  minValue,
  required
} from '../../common/helpers/validators'
import './GiftForm.scss'
import { IFormFieldProps, IFormInputProps, IGiftFormProps } from './models'

/* tslint:disable: object-literal-sort-keys */
const labelColSize = {
  xs: 24,
  md: 5,
  lg: 3,
  xxl: 2
}

const inputColSize = {
  md: 15,
  lg: 12,
  xxl: 8,
}
/* tslint:enable: object-literal-sort-keys */

const FormField = ({ meta, label, children }: IFormFieldProps) => (
  <Row className='form__field'>
    <Col {...labelColSize}>
      <label>{label}</label>
    </Col>
    <Col {...inputColSize}>
      {children}
      {(meta.error || meta.submitError) && meta.touched && (
        <span className='text-danger'>{meta.error || meta.submitError}</span>
      )}
    </Col>
  </Row>
)

const renderInput = ({
  input,
  meta,
  label
}: IFormInputProps) => (
    <FormField meta={meta} label={label}>
      <Input {...input} />
    </FormField>
  )

const renderTextArea = ({
  input,
  meta,
  label
}: IFormInputProps) => (
    <FormField meta={meta} label={label}>
      <TextArea {...input} />
    </FormField>
  )

const renderFileInput = ({
  input,
  meta,
  label,
  currentImageUrl
}: IFormInputProps & { currentImageUrl?: string }) => {
  // Handle both old API URLs and new MinIO URLs
  const getDisplayImageUrl = (url?: string) => {
    if (!url) return undefined
    
    // If it's an old API URL, convert to MinIO public URL
    if (url.includes('/api/gifts/serve-image/')) {
      const pathParts = url.split('/api/gifts/serve-image/')[1]
      return `https://data4.ikara.co/data/minio/ikara-data/uploads/${pathParts}`
    }
    
    // If it's an old MinIO API URL, convert to public URL
    if (url.includes('data4.ikara.co:9000/ikara-data/')) {
      const pathParts = url.split('data4.ikara.co:9000/ikara-data/')[1]
      return `https://data4.ikara.co/data/minio/ikara-data/${pathParts}`
    }
    
    // If it's already a public MinIO URL, use it directly
    if (url.includes('data4.ikara.co/data/minio/')) {
      return url
    }
    
    // For any other URL, return as is
    return url
  }

  const displayImageUrl = getDisplayImageUrl(currentImageUrl)

  return (
    <FormField meta={meta} label={label}>
      {displayImageUrl && (
        <div style={{ marginBottom: '10px' }}>
          <p>Ảnh hiện tại:</p>
          <Image
            src={displayImageUrl}
            alt="Current gift image"
            style={{ maxWidth: '200px', maxHeight: '200px' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
            onError={(e) => {
              console.error('Image load error:', e)
              console.error('Image URL:', displayImageUrl)
            }}
          />
        </div>
      )}
      <input type="file"
        accept="image/*"
        onChange={e => input.onChange(e.target.files)}
      />
      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
        {displayImageUrl ? 'Chọn ảnh mới để thay thế ảnh hiện tại' : 'Chọn ảnh cho quà'}
      </p>
    </FormField>
  )
}

const GiftForm = ({
  onSubmit,
  onCancel,
  loading,
  initialValues
}: IGiftFormProps) => (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Field name='name' validate={required<string>()}>
            {({ input, meta }) => renderInput({
              input,
              label: 'Name',
              meta
            })}
          </Field>
          <Field name='description'>
            {({ input, meta }) => renderTextArea({
              input,
              label: 'Description',
              meta
            })}
          </Field>
          <Field
            name='amount'
            type='number'
            validate={composeValidators(required(), minValue(0))}
          >
            {({ input, meta }) => renderInput({
              input,
              label: 'Stock',
              meta
            })}
          </Field>
          <Field
            name='cost'
            type='number'
            validate={composeValidators(required(), minValue(1))}
          >
            {({ input, meta }) => renderInput({
              input,
              label: 'Cost',
              meta
            })}
          </Field>
          <Field
            type='file'
            name='files'
            validate={fileKbSizeValid(500)}
          >
            {({ input, meta }) => renderFileInput({
              input,
              label: 'Image',
              meta,
              currentImageUrl: initialValues?.imgUrl
            })}
          </Field>
          <Row className='form__buttons'>
            <Button type='primary' htmlType='submit' loading={loading}>
              Save
          </Button>
            <Button
              htmlType='button'
              onClick={onCancel}
              className='form__button--add'
            >
              Cancel
          </Button>
          </Row>
        </form>
      )}
    />
  )

export default GiftForm

